const DB_NAME = "labelAutoPrint";
const STORE_NAME = "label_preferences";

// Custom event for notifying when label auto-print preferences change
export const LABEL_AUTO_PRINT_CHANGE_EVENT = "labelAutoPrintChange";

export function dispatchLabelAutoPrintChange(): void {
  window.dispatchEvent(new CustomEvent(LABEL_AUTO_PRINT_CHANGE_EVENT));
}

interface LabelPreference {
  orderId: string;
  disabled: boolean;
  manuallyPrinted?: boolean;
  timestamp: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject("Error opening DB");
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      db.createObjectStore(STORE_NAME, { keyPath: "orderId" });
    };
  });
}

// Clean up entries older than 1 month
async function cleanupOldEntries(): Promise<void> {
  const db = await openDb();
  const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        const record = cursor.value as LabelPreference;
        if (record.timestamp < oneMonthAgo) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject("Error cleaning up old entries");
  });
}

export async function setLabelAutoPrintDisabled(orderId: string, disabled: boolean): Promise<void> {
  // Clean up old entries before making changes
  await cleanupOldEntries();

  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    // Only save when disabled=true (exception to default behavior)
    // Delete entry when disabled=false (revert to default enabled behavior)
    if (disabled) {
      const preference: LabelPreference = {
        orderId,
        disabled: true,
        timestamp: Date.now(),
      };
      const request = store.put(preference);
      request.onsuccess = () => {
        dispatchLabelAutoPrintChange();
        resolve();
      };
      request.onerror = () => reject("Error setting label auto-print preference");
    } else {
      const request = store.delete(orderId);
      request.onsuccess = () => {
        dispatchLabelAutoPrintChange();
        resolve();
      };
      request.onerror = () => reject("Error removing label auto-print preference");
    }
  });
}

export async function isLabelAutoPrintDisabled(orderId: string): Promise<boolean> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(orderId);
    request.onsuccess = () => {
      const result = request.result as LabelPreference | undefined;
      resolve(result?.disabled ?? false);
    };
    request.onerror = () => reject("Error getting label auto-print preference");
  });
}

export async function removeLabelPreference(orderId: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(orderId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject("Error removing label auto-print preference");
  });
}

export async function setManuallyPrinted(orderId: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    // First get existing preference
    const getRequest = store.get(orderId);
    getRequest.onsuccess = () => {
      const existing = getRequest.result as LabelPreference | undefined;
      const preference: LabelPreference = {
        orderId,
        disabled: existing?.disabled ?? true,
        manuallyPrinted: true,
        timestamp: Date.now(),
      };
      const putRequest = store.put(preference);
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject("Error setting manually printed");
    };
    getRequest.onerror = () => reject("Error getting existing preference");
  });
}

export async function wasManuallyPrinted(orderId: string): Promise<boolean> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(orderId);
    request.onsuccess = () => {
      const result = request.result as LabelPreference | undefined;
      resolve(result?.manuallyPrinted ?? false);
    };
    request.onerror = () => reject("Error checking manually printed status");
  });
}
